import type { FinancialContext, MemberBalance } from '../../../services/contextApi';
import { canLinkTelegramGroup, canManageMembers } from '../../../utils/contextPermissions';

export type GroupSetupStepKey = 'telegram' | 'invite' | 'firstExpense';

export interface GroupSetupStep {
  key: GroupSetupStepKey;
  done: boolean;
  /** Whether this user has a way to do it; a member can't connect or invite. */
  canAct: boolean;
}

export interface GroupSetupState {
  steps: GroupSetupStep[];
  /** The first step not done that the user can do: the one with the contained button. */
  next: GroupSetupStepKey | null;
  /** Owner or admin: connects Telegram and invites. */
  manages: boolean;
  /** The linked Telegram group, if there is one. */
  chat: NonNullable<FinancialContext['telegramChats']>[number] | null;
}

interface Input {
  group: Pick<FinancialContext, 'memberRole' | 'telegramChats'>;
  /** Everyone in the group's balances, invited ones included. */
  members: Pick<MemberBalance, 'userId'>[];
  /** Invitations to sign up nobody has taken up yet; only owners and admins can see them. */
  signupInvitations?: number;
  hasEntries?: boolean;
}

/**
 * Where a new group stands in "Monte o grupo": linked to a Telegram group,
 * someone else in it (or invited), and a first expense. Each step says
 * whether this user can do it, so a member is never shown a button that
 * would be refused.
 */
export const groupSetupSteps = ({ group, members, signupInvitations = 0, hasEntries = false }: Input): GroupSetupState => {
  const role = group.memberRole;
  const manages = canManageMembers(role);
  const chat = group.telegramChats?.[0] ?? null;

  const steps: GroupSetupStep[] = [
    { key: 'telegram', done: Boolean(chat), canAct: canLinkTelegramGroup(role) },
    // An invitation to sign up counts too: that person has no account yet, so isn't in the balances.
    { key: 'invite', done: members.length > 1 || (manages && signupInvitations > 0), canAct: manages },
    { key: 'firstExpense', done: hasEntries, canAct: role !== 'viewer' },
  ];

  return {
    steps,
    next: steps.find((step) => !step.done && step.canAct)?.key ?? null,
    manages,
    chat,
  };
};
